import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  queryClient,
  apiRequest,
  fetchAdminStaffListJson,
  ADMIN_LIST_PAGE_SIZE,
  resetAdminStaffQueries,
} from '@/lib/queryClient';
import { useAdminStaffCatalog } from '@/hooks/useAdminPagedCatalogs';
import { useStaffListWorkingHours, isPlaceholderStaffId } from '@/hooks/useStaffListWorkingHours';
import { useToast } from '@/hooks/useToast';
import Box from '@mui/material/Box';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Skeleton from '@mui/material/Skeleton';
import TextField from '@mui/material/TextField';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import { Add, Close, Delete, Edit, Email, People } from '@mui/icons-material';
import { getInitials, DAYS_OF_WEEK } from '@/lib/utils';
import TimeTextField from '@/components/TimeTextField';
import {
  validateRequiredName,
  validateEmailRequired,
  validateOptionalPhone,
  compareTimeHHmm,
} from '@/lib/formValidation';
import {
  dialogActionsSx,
  dialogContentOutlinedFormSx,
  dialogPaperPropsLabelSafe,
} from '@/lib/dialogStyles';
import { computeAdminListRange, normalizeAdminStaffListPayload } from '@/lib/adminApi';
import type { Staff } from '@shared/schema';

interface StaffFormData {
  displayName: string;
  email: string;
  phone: string;
  role: string;
  bio: string;
  active: boolean;
  selectedEposNowId: string;
  workingHours: {
    dayOfWeek: number;
    startTime: string;
    endTime: string;
    isActive: boolean;
  }[];
}

/** Swagger `CreateStaffBody`: `eposNowStaffId`, optional `email`, `active`, `workingHours`. */
function buildCreateStaffBody(formData: StaffFormData): Record<string, unknown> {
  const body: Record<string, unknown> = {
    eposNowStaffId: Number(formData.selectedEposNowId),
    active: formData.active,
    workingHours: formData.workingHours,
  };
  const email = formData.email.trim();
  if (email) body.email = email;
  return body;
}

const defaultWorkingHours = DAYS_OF_WEEK.map((_, i) => ({
  dayOfWeek: i,
  startTime: '09:00',
  endTime: '17:00',
  isActive: i >= 1 && i <= 5,
}));

const defaultFormData: StaffFormData = {
  displayName: '',
  email: '',
  phone: '',
  role: 'staff',
  bio: '',
  active: true,
  selectedEposNowId: '',
  workingHours: defaultWorkingHours,
};

interface StaffFieldErrors {
  displayName?: string;
  email?: string;
  phone?: string;
  workingHours?: string;
  eposNowId?: string;
}

export default function AdminStaff() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  /** Latest `dialogOpen` for `Transition` onExited — avoid clearing edit state if user reopened before exit finished. */
  const dialogOpenRef = useRef(dialogOpen);
  dialogOpenRef.current = dialogOpen;
  const [formData, setFormData] = useState<StaffFormData>(defaultFormData);
  const [fieldErrors, setFieldErrors] = useState<StaffFieldErrors>({});
  const [staffPage, setStaffPage] = useState(1);

  const { data: staffPayload, isLoading } = useQuery({
    queryKey: ['/api/admin/staff', 'paged', staffPage, ADMIN_LIST_PAGE_SIZE],
    queryFn: () => fetchAdminStaffListJson(staffPage, ADMIN_LIST_PAGE_SIZE),
    select: (data: unknown) => normalizeAdminStaffListPayload(data),
  });
  const staffList = staffPayload?.staff ?? [];
  const staffListMeta = staffPayload?.metadata;

  useEffect(() => {
    const tp = staffListMeta?.totalPages;
    if (tp != null && staffPage > tp) setStaffPage(Math.max(1, tp));
  }, [staffListMeta?.totalPages, staffPage]);

  const { hoursByStaffIndex } = useStaffListWorkingHours(staffList);

  const { staff: staffCatalogFull } = useAdminStaffCatalog();

  /** Staff rows from GET `/staff` that include `eposNowId` (swagger `StaffResponse`). */
  const eposStaffOptions = useMemo(
    () => staffCatalogFull.filter((s) => s.eposNowId != null && !Number.isNaN(Number(s.eposNowId))),
    [staffCatalogFull]
  );

  function closeDialog() {
    setDialogOpen(false);
  }

  /** Reset form identity only after close animation — if we clear `editingId` with `open`, the title flashes "New" while the panel is still visible. */
  function handleDialogExited() {
    if (!dialogOpenRef.current) {
      setEditingId(null);
      setFieldErrors({});
    }
  }

  const createMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => apiRequest('POST', '/api/admin/staff', body),
    onSuccess: async () => {
      await resetAdminStaffQueries(queryClient);
      toast({ title: t('adminStaff.toastAdded') });
      closeDialog();
    },
    onError: (err: Error) =>
      toast({ title: t('common.error'), description: err.message, variant: 'destructive' }),
  });

  const updateMutation = useMutation({
    mutationFn: (data: StaffFormData) => {
      if (!editingId) throw new Error('update without id');
      return apiRequest('PATCH', `/api/admin/staff/${editingId}`, data);
    },
    onSuccess: async () => {
      await resetAdminStaffQueries(queryClient);
      toast({ title: t('adminStaff.toastUpdated') });
      closeDialog();
    },
    onError: (err: Error) =>
      toast({ title: t('common.error'), description: err.message, variant: 'destructive' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => {
      if (isPlaceholderStaffId(id)) throw new Error('cannot delete placeholder staff');
      return apiRequest('DELETE', `/api/admin/staff/${id}`);
    },
    onSuccess: async () => {
      await resetAdminStaffQueries(queryClient);
      toast({ title: t('adminStaff.toastRemoved') });
    },
  });

  function openCreate() {
    setEditingId(null);
    setFormData({ ...defaultFormData, workingHours: defaultWorkingHours });
    setFieldErrors({});
    setDialogOpen(true);
  }

  function openEdit(member: Staff, memberIndex: number) {
    setEditingId(member.id);
    const memberHours = hoursByStaffIndex?.[memberIndex] ?? [];
    const hours = DAYS_OF_WEEK.map((_, i) => {
      const existing = memberHours.find((wh) => wh.dayOfWeek === i);
      return existing
        ? {
            dayOfWeek: i,
            startTime: existing.startTime,
            endTime: existing.endTime,
            isActive: existing.isActive !== false,
          }
        : { dayOfWeek: i, startTime: '09:00', endTime: '17:00', isActive: false };
    });
    setFormData({
      displayName: member.displayName,
      email: member.email || '',
      phone: member.phone || '',
      role: member.role || 'staff',
      bio: member.bio || '',
      active: member.active !== false,
      selectedEposNowId: member.eposNowId != null ? String(member.eposNowId) : '',
      workingHours: hours,
    });
    setFieldErrors({});
    setDialogOpen(true);
  }

  function validateStaff(): boolean {
    const next: StaffFieldErrors = {};
    const isAddMode = editingId === null;

    if (isAddMode) {
      if (!formData.selectedEposNowId || Number.isNaN(Number(formData.selectedEposNowId))) {
        next.eposNowId = t('adminStaff.eposStaffSelectRequired');
      }
    } else {
      const nameReadOnlyEdit = editingId != null && !isPlaceholderStaffId(editingId);
      if (!nameReadOnlyEdit) {
        const nameErr = validateRequiredName(formData.displayName, t('adminStaff.name'));
        if (nameErr) next.displayName = nameErr;
      }
    }

    const emailErr = validateEmailRequired(formData.email);
    if (emailErr) next.email = emailErr;
    const phoneErr = validateOptionalPhone(formData.phone);
    if (phoneErr) next.phone = phoneErr;

    for (let i = 0; i < formData.workingHours.length; i++) {
      const wh = formData.workingHours[i];
      if (!wh.isActive) continue;
      if (compareTimeHHmm(wh.endTime, wh.startTime) <= 0) {
        next.workingHours = t('adminStaff.timeOrderError', { day: t(`daysOfWeek.${i}`) });
        break;
      }
    }
    setFieldErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleSubmit() {
    if (!validateStaff()) return;
    if (editingId == null || isPlaceholderStaffId(editingId)) {
      createMutation.mutate(buildCreateStaffBody(formData));
    } else {
      updateMutation.mutate(formData);
    }
  }

  function updateWorkingHour(index: number, field: string, value: unknown) {
    const updated = [...formData.workingHours];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, workingHours: updated });
    setFieldErrors((prev) => (prev.workingHours ? { ...prev, workingHours: undefined } : prev));
  }

  const outlinedLabelSx = {
    bgcolor: 'background.paper' as const,
    px: 0.75,
    borderRadius: 0.5,
  };

  const staffGridSx = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
    gap: 2,
    width: '100%',
  } as const;

  function roleLabel(role: string | null | undefined) {
    const r = (role || 'staff').toLowerCase();
    if (r === 'manager') return t('adminStaff.roleManager');
    if (r === 'owner') return t('adminStaff.roleOwner');
    return t('adminStaff.roleStaff');
  }

  const showAddStyleForm =
    editingId === null || (editingId != null && isPlaceholderStaffId(editingId));

  const staffListRange = computeAdminListRange(staffListMeta, staffList.length);

  return (
    <Box sx={{ p: 3, maxWidth: { xs: '100%', lg: 1400 }, mx: 'auto', width: '100%' }}>
      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}
      >
        <Box>
          <Typography variant="h5" fontWeight="bold" data-testid="text-staff-title">
            {t('adminStaff.title')}
          </Typography>
          <Typography color="text.secondary">{t('adminStaff.subtitle')}</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={openCreate}
          data-testid="button-add-staff"
        >
          {t('adminStaff.addStaff')}
        </Button>
      </Box>

      {isLoading ? (
        <Box sx={staffGridSx}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} height={128} variant="rounded" />
          ))}
        </Box>
      ) : staffList?.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <People sx={{ fontSize: 48, color: 'action.disabled', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {t('adminStaff.emptyTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('adminStaff.emptyBody')}
            </Typography>
            <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
              {t('adminStaff.addStaff')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Box sx={staffGridSx}>
          {staffList?.map((member, index) => {
            const memberHours = hoursByStaffIndex?.[index]?.filter((wh) => wh.isActive) || [];
            return (
              <Card
                key={`staff-row-${index}-${member.eposNowId ?? ''}-${member.id}`}
                sx={{ opacity: member.active ? 1 : 0.65, height: '100%' }}
              >
                <CardContent sx={{ height: '100%' }}>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                    <Avatar sx={{ width: 48, height: 48 }}>
                      {getInitials(member.displayName)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography fontWeight={600} data-testid={`text-staff-name-${member.id}`}>
                          {member.displayName}
                        </Typography>
                        <Chip size="small" label={roleLabel(member.role)} />
                        {!member.active && (
                          <Chip size="small" label={t('adminStaff.inactive')} variant="outlined" />
                        )}
                      </Box>
                      {member.email && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}
                        >
                          <Email sx={{ fontSize: 14 }} />
                          {member.email}
                        </Typography>
                      )}
                      {memberHours.length > 0 && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 0.5, display: 'block' }}
                        >
                          {t('adminStaff.worksDays', {
                            days: memberHours
                              .map((h) => t(`daysOfWeek.${h.dayOfWeek}`).slice(0, 3))
                              .join(', '),
                          })}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => openEdit(member, index)}
                        data-testid={`button-edit-staff-${member.id}-${index}`}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      {!isPlaceholderStaffId(member.id) && (
                        <IconButton
                          size="small"
                          onClick={() => deleteMutation.mutate(member.id)}
                          disabled={deleteMutation.isPending}
                          data-testid={`button-delete-staff-${member.id}`}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}

      {!isLoading &&
        staffList.length > 0 &&
        staffListMeta?.totalRecords != null &&
        staffListMeta.totalRecords > 0 && (
          <Box
            sx={{
              mt: 2,
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
            data-testid="box-staff-pagination"
          >
            <Typography variant="body2" color="text.secondary" component="div">
              {staffListRange
                ? t('adminCommon.paginationRange', {
                    start: staffListRange.start,
                    end: staffListRange.end,
                    total: staffListRange.total,
                  })
                : null}
            </Typography>
            {staffListMeta.totalPages != null && staffListMeta.totalPages > 1 && (
              <Pagination
                color="primary"
                size="small"
                count={Math.max(1, staffListMeta.totalPages)}
                page={staffListMeta?.page ?? staffPage}
                onChange={(_, p) => setStaffPage(p)}
              />
            )}
          </Box>
        )}

      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        PaperProps={dialogPaperPropsLabelSafe}
        TransitionProps={{ onExited: handleDialogExited }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            pr: 1,
          }}
        >
          <span>
            {editingId && isPlaceholderStaffId(editingId)
              ? t('adminStaff.completeMember')
              : editingId
              ? t('adminStaff.editMember')
              : t('adminStaff.newMember')}
          </span>
          <IconButton onClick={closeDialog} aria-label={t('common.close')} size="small" edge="end">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={dialogContentOutlinedFormSx}>
          {showAddStyleForm ? (
            <>
              <TextField
                select
                required
                label={t('adminStaff.eposStaffSelect')}
                value={formData.selectedEposNowId}
                onChange={(e) => {
                  setFormData({ ...formData, selectedEposNowId: e.target.value });
                  if (fieldErrors.eposNowId)
                    setFieldErrors((prev) => ({ ...prev, eposNowId: undefined }));
                }}
                error={Boolean(fieldErrors.eposNowId)}
                helperText={fieldErrors.eposNowId}
                fullWidth
                data-testid="select-epos-staff"
                InputLabelProps={{ sx: outlinedLabelSx }}
              >
                <MenuItem value="" disabled>
                  {t('adminStaff.eposStaffPlaceholder')}
                </MenuItem>
                {eposStaffOptions.map((row) => (
                  <MenuItem key={`${row.id}-${row.eposNowId}`} value={String(row.eposNowId)}>
                    {row.displayName}
                    {row.email ? ` · ${row.email}` : ''}
                  </MenuItem>
                ))}
              </TextField>
              <Box
                sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}
              >
                <TextField
                  required
                  label={t('adminStaff.email')}
                  placeholder={t('adminStaff.emailPlaceholder')}
                  type="email"
                  value={formData.email}
                  onChange={(e) => {
                    setFormData({ ...formData, email: e.target.value });
                    if (fieldErrors.email)
                      setFieldErrors((prev) => ({ ...prev, email: undefined }));
                  }}
                  error={Boolean(fieldErrors.email)}
                  helperText={fieldErrors.email}
                  data-testid="input-staff-email"
                  fullWidth
                  InputLabelProps={{ sx: outlinedLabelSx }}
                />
                <TextField
                  label={t('adminStaff.phone')}
                  value={formData.phone}
                  onChange={(e) => {
                    setFormData({ ...formData, phone: e.target.value });
                    if (fieldErrors.phone)
                      setFieldErrors((prev) => ({ ...prev, phone: undefined }));
                  }}
                  error={Boolean(fieldErrors.phone)}
                  helperText={fieldErrors.phone}
                  fullWidth
                  InputLabelProps={{ sx: outlinedLabelSx }}
                />
              </Box>
            </>
          ) : (
            <>
              <Box sx={{ mb: 3 }} data-testid="text-staff-name-display">
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 0.5 }}
                >
                  {t('adminStaff.name')}
                </Typography>
                <Typography variant="body1" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                  {formData.displayName || '—'}
                </Typography>
              </Box>
              <Box
                sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}
              >
                <TextField
                  required
                  label={t('adminStaff.email')}
                  placeholder={t('adminStaff.emailPlaceholder')}
                  type="email"
                  value={formData.email}
                  onChange={(e) => {
                    setFormData({ ...formData, email: e.target.value });
                    if (fieldErrors.email)
                      setFieldErrors((prev) => ({ ...prev, email: undefined }));
                  }}
                  error={Boolean(fieldErrors.email)}
                  helperText={fieldErrors.email}
                  data-testid="input-staff-email"
                  fullWidth
                  InputLabelProps={{ sx: outlinedLabelSx }}
                />
                <TextField
                  label={t('adminStaff.phone')}
                  value={formData.phone}
                  onChange={(e) => {
                    setFormData({ ...formData, phone: e.target.value });
                    if (fieldErrors.phone)
                      setFieldErrors((prev) => ({ ...prev, phone: undefined }));
                  }}
                  error={Boolean(fieldErrors.phone)}
                  helperText={fieldErrors.phone}
                  fullWidth
                  InputLabelProps={{ sx: outlinedLabelSx }}
                />
              </Box>
            </>
          )}
          <Typography variant="subtitle2" fontWeight={600}>
            {t('adminStaff.workingHours')}
          </Typography>
          {fieldErrors.workingHours && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: -1 }}>
              {fieldErrors.workingHours}
            </Typography>
          )}
          <Stack spacing={1}>
            {formData.workingHours.map((wh, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Switch
                  checked={wh.isActive}
                  onChange={(_, c) => updateWorkingHour(i, 'isActive', c)}
                />
                <Typography variant="body2" sx={{ width: 40, flexShrink: 0 }}>
                  {t(`daysOfWeekShort.${i}`)}
                </Typography>
                {wh.isActive ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                    <TimeTextField
                      size="small"
                      value={wh.startTime}
                      onChange={(e) => updateWorkingHour(i, 'startTime', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                    <Typography variant="caption">{t('common.to')}</Typography>
                    <TimeTextField
                      size="small"
                      value={wh.endTime}
                      onChange={(e) => updateWorkingHour(i, 'endTime', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t('common.off')}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
          <FormControlLabel
            control={
              <Switch
                checked={formData.active}
                onChange={(_, c) => setFormData({ ...formData, active: c })}
              />
            }
            label={t('adminStaff.active')}
          />
        </DialogContent>
        <DialogActions sx={dialogActionsSx}>
          <Button variant="outlined" onClick={closeDialog}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={createMutation.isPending || updateMutation.isPending}
            data-testid="button-save-staff"
          >
            {createMutation.isPending || updateMutation.isPending
              ? t('common.saving')
              : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
